/**
 * Promise-based wrapper around the legacy callback-based API class.
 *
 * Each function reads `window.api` at call time, wraps the callback in a Promise,
 * and returns the result. This is purely additive — the original API.ts is untouched.
 *
 * Usage from React (ES module):
 *   import { getCallCardById, saveCallCard } from '@/services/api';
 *   const card = await getCallCardById(123, false);
 *
 * Usage from legacy code (window global):
 *   const card = await window.OrnaReact.api.getCallCardById(123, false);
 */

import type {
  CallCardData,
  CallCommentData,
  MapOverlayGetResult,
  NpsdType,
} from './api-types';

// Shorthand to access the legacy API singleton
function getApi(): any {
  return (window as any).api;
}

// ── Authentication & Session ──────────────────────────────────────────

export function login(username: string, password: string): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().login(username, password, resolve, reject);
  });
}

export function logout(): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().logout(resolve, reject);
  });
}

export function isLoggedIn(): Promise<boolean> {
  return new Promise((resolve) => {
    getApi().isLoggedIn((result: { state: string }) => resolve(result.state !== 'no'));
  });
}

export function keepAlive(): void {
  getApi().keepAlive();
}

export function reauthorizeRealtime(): Promise<any> {
  return new Promise((resolve) => {
    getApi().reauthorizeRealtime(resolve);
  });
}

export function getChatHistory(): Promise<any> {
  return new Promise((resolve) => {
    getApi().getChatHistory(resolve);
  });
}

export function sendChatMessage(entry: string, chatType: number): Promise<any> {
  return new Promise((resolve) => {
    getApi().sendChatMessage({ entry, chatType }, resolve);
  });
}

// ── Home & Module ─────────────────────────────────────────────────────

export function getHome(): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().getHome(resolve, reject);
  });
}

export function selectModule(module: any): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().selectModule(module, resolve, reject);
  });
}

// ── Call Cards ────────────────────────────────────────────────────────

export function getCallCards(moduleType: string, type: string, start: number, perPage: string): Promise<any> {
  return new Promise((resolve) => {
    getApi().getCallCards(moduleType, type, start, perPage, resolve);
  });
}

export function getPanicCallCards(start: number, perPage: string, noLoadScreen: boolean = true): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().getPanicCallCards(start, perPage, resolve, reject, noLoadScreen);
  });
}

export function callCardPdf(callCardId: number, timeZoneId: string): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().callCardPdf(callCardId, timeZoneId, resolve, reject);
  });
}

export function getAvailableCADOfficers(callCardId: number, noLoadScreen: boolean = true): Promise<any> {
  return new Promise((resolve) => {
    getApi().getAvailableCADOfficers(callCardId, resolve, noLoadScreen);
  });
}

export function getAvailableMDTOfficers(): Promise<any> {
  return new Promise((resolve) => {
    getApi().getAvailableMDTOfficers(resolve);
  });
}

export function getCallCardSearchOfficers(): Promise<any> {
  return new Promise((resolve) => {
    getApi().getCallCardSearchOfficers(resolve);
  });
}

export function getReportSearchOfficers(): Promise<any> {
  return new Promise((resolve) => {
    getApi().getReportSearchOfficers(resolve);
  });
}

export function getManualStatus(): Promise<any> {
  return new Promise((resolve) => {
    getApi().getManualStatus(resolve);
  });
}

export function updateManualStatus(status: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().updateManualStatus(status, resolve);
  });
}

export function listCallComments(callCardId: number): Promise<CallCommentData[]> {
  return new Promise((resolve, reject) => {
    getApi().listCallComments(callCardId, resolve, reject);
  });
}

export function saveCallComment(
  callCardId: number,
  entry: string,
  saveOnClosedCall: boolean
): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().saveCallComment(callCardId, entry, saveOnClosedCall, resolve, reject);
  });
}

export function updateCallComment(
  callCommentId: number,
  entry: string,
  updateOnClosedCall: boolean
): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().updateCallComment(callCommentId, entry, updateOnClosedCall, resolve, reject);
  });
}

export function getCallCardById(callCardId: any, isLog: boolean): Promise<any> {
  return new Promise((resolve) => {
    getApi().getCallCardById(callCardId, isLog, resolve);
  });
}

export function saveCallCard(callCard: any): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().saveCallCard(callCard, resolve, reject);
  });
}

export function updateCallCard(callCard: any): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().updateCallCard(callCard, resolve, reject);
  });
}

export function callCardReportDecision(callCardId: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().callCardReportDecision(callCardId, resolve);
  });
}

export function updateMDTCallCard(id: any, data: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().updateMDTCallCard(id, data, resolve);
  });
}

export function getMDTCallCard(id: string, isLog: boolean): Promise<any> {
  return new Promise((resolve) => {
    getApi().getMDTCallCard(id, isLog, resolve);
  });
}

export function reviseCallCard(callCard: any): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().reviseCallCard(callCard, resolve, reject);
  });
}

export function closeCallCard(callCard: any): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().closeCallCard(callCard, resolve, reject);
  });
}

export function reopenCallCard(callCardId: number): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().reopenCallCard(callCardId, resolve, reject);
  });
}

export function getCallCardAuditLogEntries(
  callCardId: number,
  start: number,
  perPage: string,
  data: any
): Promise<any> {
  return new Promise((resolve) => {
    getApi().getCallCardAuditLogEntries(callCardId, start, perPage, data, resolve);
  });
}

export function forceOfficer(
  callCardId: number,
  officerId: string,
  authTokenRefresh: boolean
): Promise<any> {
  return new Promise((resolve) => {
    getApi().forceOfficer(callCardId, officerId, authTokenRefresh, resolve);
  });
}

export function saveCallCardFromPanicButton(): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().saveCallCardFromPanicButton(resolve, reject);
  });
}

// ── Radio Log ─────────────────────────────────────────────────────────

export function getRadioLogEntries(
  start: number,
  perPage: string,
  data: string,
  authTokenRefresh: boolean
): Promise<any> {
  return new Promise((resolve) => {
    getApi().getRadioLogEntries(start, perPage, data, authTokenRefresh, resolve);
  });
}

export function addRadioLogEntry(entry: string): Promise<any> {
  return new Promise((resolve) => {
    getApi().addRadioLogEntry(entry, resolve);
  });
}

// ── Audit Log ─────────────────────────────────────────────────────────

export function getAuditLogEntries(
  id: number,
  start: number,
  perPage: string,
  data: any
): Promise<any> {
  return new Promise((resolve) => {
    getApi().getAuditLogEntries(id, start, perPage, data, resolve);
  });
}

export function getNPSDAuditLogEntries(
  id: number,
  npsdType: string,
  npsdTypeIdField: string,
  start: number,
  perPage: string,
  data: any
): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().getNPSDAuditLogEntries(id, npsdType, npsdTypeIdField, start, perPage, data, resolve, reject);
  });
}

export function getNotesAuditLogEntries(
  companyId: number,
  start: number,
  perPage: string,
  data: any
): Promise<any> {
  return new Promise((resolve) => {
    getApi().getNotesAuditLogEntries(companyId, start, perPage, data, resolve);
  });
}

// ── NPSD ──────────────────────────────────────────────────────────────

export function getNPSDItemById(id: string, npsdType: string): Promise<any> {
  return new Promise((resolve) => {
    getApi().getNPSDItemById(id, npsdType, resolve);
  });
}

export function getNPSDClientAddress(id: number): Promise<any> {
  return new Promise((resolve) => {
    getApi().getNPSDClientAddress(id, resolve);
  });
}

export function lockNPSDItem(id: string, isLocked: boolean, npsdType: string): Promise<any> {
  return new Promise((resolve) => {
    getApi().lockNPSDItem(id, isLocked, npsdType, resolve);
  });
}

export function saveNPSDItem(item: any, npsdType: string): Promise<any> {
  return new Promise((resolve) => {
    getApi().saveNPSDItem(item, npsdType, resolve);
  });
}

export function updateNPSDItem(item: any, npsdType: string): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().updateNPSDItem(item, npsdType, resolve, reject);
  });
}

export function updateRelationships(
  relType: string,
  id: string,
  connectionsOld: any,
  connectionsNew: any
): Promise<any> {
  return new Promise((resolve) => {
    getApi().updateRelationships(relType, id, connectionsOld, connectionsNew, resolve);
  });
}

// ── Search ────────────────────────────────────────────────────────────

export function npsdSearch(
  searchType: string,
  start: number,
  perPage: string,
  data: any
): Promise<any> {
  return new Promise((resolve) => {
    getApi().npsdSearch(searchType, start, perPage, data, resolve);
  });
}

export function callCardSearch(
  searchType: string,
  start: number,
  perPage: string,
  data: string
): Promise<any> {
  return new Promise((resolve) => {
    getApi().callCardSearch(searchType, start, perPage, data, resolve);
  });
}

export function callCardSearchByNpsd(
  npsdDataId: number,
  npsdType: string,
  npsdFieldId: string,
  start: number,
  perPage: string,
  data: string
): Promise<any> {
  return new Promise((resolve) => {
    getApi().callCardSearchByNpsd(npsdDataId, npsdType, npsdFieldId, start, perPage, data, resolve);
  });
}

export function callCardSearchByLocation(
  callLocFormattedAddress: string,
  start: number,
  perPage: string,
  data: string
): Promise<any> {
  return new Promise((resolve) => {
    getApi().callCardSearchByLocation(callLocFormattedAddress, start, perPage, data, resolve);
  });
}

export function reportSearch(
  searchType: string,
  start: number,
  perPage: string,
  data: string
): Promise<any> {
  return new Promise((resolve) => {
    getApi().reportSearch(searchType, start, perPage, data, resolve);
  });
}

export function reportSearchByLocation(
  callLocFormattedAddress: string,
  start: number,
  perPage: string,
  data: string
): Promise<any> {
  return new Promise((resolve) => {
    getApi().reportSearchByLocation(callLocFormattedAddress, start, perPage, data, resolve);
  });
}

export function notesSearch(
  start: number,
  perPage: string,
  data: string
): Promise<any> {
  return new Promise((resolve) => {
    getApi().notesSearch(start, perPage, data, resolve);
  });
}

// ── Notes ─────────────────────────────────────────────────────────────

export function saveNote(data: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().saveNote(data, resolve);
  });
}

export function updateNote(noteId: number, data: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().updateNote(noteId, data, resolve);
  });
}

export function deleteNote(noteId: number): Promise<any> {
  return new Promise((resolve) => {
    getApi().deleteNote(noteId, resolve);
  });
}

export function getNoteOnBehalfOfUsers(): Promise<any> {
  return new Promise((resolve) => {
    getApi().getNoteOnBehalfOfUsers(resolve);
  });
}

// ── Activity Reports ──────────────────────────────────────────────────

export function getActivityReportData(data: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().getActivityReportData(data, resolve);
  });
}

export function getMileageActivityReportData(data: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().getMileageActivityReportData(data, resolve);
  });
}

export function getActivityReportPDFBase64(data: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().getActivityReportPDFBase64(data, resolve);
  });
}

// ── Password & User ───────────────────────────────────────────────────

export function updatePassword(data: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().updatePassword(data, resolve);
  });
}

export function passwordReset(userId: string): Promise<any> {
  return new Promise((resolve) => {
    getApi().passwordReset(userId, resolve);
  });
}

export function recoverUsername(data: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().recoverUsername(data, resolve);
  });
}

export function passwordResetByUsername(data: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().passwordResetByUsername(data, resolve);
  });
}

export function unsubscribeEmails(data: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().unsubscribeEmails(data, resolve);
  });
}

// ── MDT ───────────────────────────────────────────────────────────────

export function mdtEnrouteOnSceneOrClearedNow(type: string, id: string): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().mdtEnrouteOnSceneOrClearedNow(type, id, resolve, reject);
  });
}

export function mdtActions(type: string, id: string, data: string): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().mdtActions(type, id, data, resolve, reject);
  });
}

export function mdtAddSelfToCallCard(id: string): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().mdtAddSelfToCallCard(id, resolve, reject);
  });
}

export function getMDTList(moduleType: string, type: string, start: number, perPage: string): Promise<any> {
  return new Promise((resolve) => {
    getApi().getMDTList(moduleType, type, start, perPage, resolve);
  });
}

export function getReportMDTList(type: string, start: number, perPage: string): Promise<any> {
  return new Promise((resolve) => {
    getApi().getReportMDTList(type, start, perPage, resolve);
  });
}

// ── Nature of Call ────────────────────────────────────────────────────

export function listGlobalNatureOfCalls(): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().listGlobalNatureOfCalls(resolve, reject);
  });
}

export function listNatureOfCall(includeDisabled: boolean = false): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().listNatureOfCall(resolve, reject, includeDisabled);
  });
}

export function listCompanyNatureOfCalls(): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().listCompanyNatureOfCalls(resolve, reject);
  });
}

export function getGroupAndSubgroupNocCodes(): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().getGroupAndSubgroupNocCodes(resolve, reject);
  });
}

export function getStandardOperatingProcedures(obj: object): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().getStandardOperatingProcedures(obj, resolve, reject);
  });
}

export function listCompanyNatureOfCallAttachments(
  natureOfCallCode: number,
  companyId: number
): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().listCompanyNatureOfCallAttachments(natureOfCallCode, companyId, resolve, reject);
  });
}

export function updateCompanyNatureOfCall(obj: object): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().updateCompanyNatureOfCall(obj, resolve, reject);
  });
}

export function updateCompanyNatureOfCallList(obj: object): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().updateCompanyNatureOfCallList(obj, resolve, reject);
  });
}

export function setNatureOfCallEnabled(
  natureOfCallCode: number,
  isEnabled: boolean
): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().setNatureOfCallEnabled(natureOfCallCode, isEnabled, resolve, reject);
  });
}

export function updateNatureOfCallPriority(
  natureOfCallCode: number,
  priority: number | null
): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().updateNatureOfCallPriority(natureOfCallCode, priority, resolve, reject);
  });
}

export function addCustomNatureOfCall(obj: object): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().addCustomNatureOfCall(obj, resolve, reject);
  });
}

// ── Call Alerts ────────────────────────────────────────────────────────

export function getCallAlert(obj: object): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().getCallAlert(obj, resolve, reject);
  });
}

export function listCallAlerts(): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().listCallAlerts(resolve, reject);
  });
}

export function saveCallAlert(obj: object): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().saveCallAlert(obj, resolve, reject);
  });
}

export function updateCallAlert(obj: object): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().updateCallAlert(obj, resolve, reject);
  });
}

export function deleteCallAlerts(obj: object): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().deleteCallAlerts(obj, resolve, reject);
  });
}

export function reorderCallAlerts(obj: object): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().reorderCallAlerts(obj, resolve, reject);
  });
}

export function listCallAlertTypes(): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().listCallAlertTypes(resolve, reject);
  });
}

export function updateCallAlertTypes(obj: object): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().updateCallAlertTypes(obj, resolve, reject);
  });
}

export function getCallAlertAuditLogEntries(data: any): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().getCallAlertAuditLogEntries(data, resolve, reject);
  });
}

// ── Custom Locations ──────────────────────────────────────────────────

export function listCustomLocations(isLogging: boolean): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().listCustomLocations(isLogging, resolve, reject);
  });
}

export function saveCustomLocation(obj: object): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().saveCustomLocation(obj, resolve, reject);
  });
}

export function updateCustomLocation(obj: object): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().updateCustomLocation(obj, resolve, reject);
  });
}

export function deleteCustomLocations(obj: object): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().deleteCustomLocations(obj, resolve, reject);
  });
}

export function getCustomLocationAuditLogEntries(data: any): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().getCustomLocationAuditLogEntries(data, resolve, reject);
  });
}

// ── Company Vehicles ──────────────────────────────────────────────────

export function listCompanyVehicles(
  isLogging: boolean,
  isIncludingLinkedCompanies: boolean,
  authTokenRefresh: boolean
): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().listCompanyVehicles(isLogging, isIncludingLinkedCompanies, authTokenRefresh, resolve, reject);
  });
}

export function saveCompanyVehicle(obj: object): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().saveCompanyVehicle(obj, resolve, reject);
  });
}

export function deleteCompanyVehicles(obj: object): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().deleteCompanyVehicles(obj, resolve, reject);
  });
}

export function reorderCompanyVehicles(obj: object): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().reorderCompanyVehicles(obj, resolve, reject);
  });
}

export function assignOfficerToVehicle(obj: object): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().assignOfficerToVehicle(obj, resolve, reject);
  });
}

// ── Company Call Signs ────────────────────────────────────────────────

export function listCompanyCallSigns(isLogging: boolean): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().listCompanyCallSigns(isLogging, resolve, reject);
  });
}

export function saveCompanyCallSign(obj: object): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().saveCompanyCallSign(obj, resolve, reject);
  });
}

export function deleteCompanyCallSigns(obj: object): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().deleteCompanyCallSigns(obj, resolve, reject);
  });
}

export function reorderCompanyCallSigns(obj: object): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().reorderCompanyCallSigns(obj, resolve, reject);
  });
}

export function getCallSignAuditLogEntries(data: any): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().getCallSignAuditLogEntries(data, resolve, reject);
  });
}

// ── Permissions ───────────────────────────────────────────────────────

export function getPermissionList(): Promise<any> {
  return new Promise((resolve) => {
    getApi().getPermissionList(resolve);
  });
}

export function getPermissionGroups(): Promise<any> {
  return new Promise((resolve) => {
    getApi().getPermissionGroups(resolve);
  });
}

export function updatePermissions(permissions: any): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().updatePermissions(permissions, resolve, reject);
  });
}

export function getPermissionAuditLog(data: any): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().getPermissionAuditLog(data, resolve, reject);
  });
}

// ── Reports / Case Reports ────────────────────────────────────────────

export function getReportsPendingFile(
  showAllPendingFile: boolean,
  start: number,
  perPage: string
): Promise<any> {
  return new Promise((resolve) => {
    getApi().getReportsPendingFile(showAllPendingFile, start, perPage, resolve);
  });
}

export function getReportsPendingApproval(start: number, perPage: string): Promise<any> {
  return new Promise((resolve) => {
    getApi().getReportsPendingApproval(start, perPage, resolve);
  });
}

export function getReportsHistory(start: number, perPage: string): Promise<any> {
  return new Promise((resolve) => {
    getApi().getReportsHistory(start, perPage, resolve);
  });
}

export function getCaseReportPdfUrl(reportId: number): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().getCaseReportPdfUrl(reportId, resolve, reject);
  });
}

export function unapproveCaseReport(reportId: number): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().unapproveCaseReport(reportId, resolve, reject);
  });
}

export function getReport(id: string, isLog: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().getReport(id, isLog, resolve);
  });
}

export function saveReport(report: any): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().saveReport(report, resolve, reject);
  });
}

export function updateReport(report: any, noLoadScreen: boolean): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().updateReport(report, noLoadScreen, resolve, reject);
  });
}

export function listCaseReportNarrativeAttachments(reportId: number): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().listCaseReportNarrativeAttachments(reportId, resolve, reject);
  });
}

export function deleteReport(id: string): Promise<any> {
  return new Promise((resolve) => {
    getApi().deleteReport(id, resolve);
  });
}

export function lockReport(id: string, lock: boolean): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().lockReport(id, lock, resolve, reject);
  });
}

// ── Officer Status ────────────────────────────────────────────────────

export function getOfficerStatus(authTokenRefresh: boolean): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().getOfficerStatus(authTokenRefresh, resolve, reject);
  });
}

export function getAvailableDispatchers(authTokenRefresh: boolean): Promise<any> {
  return new Promise((resolve) => {
    getApi().getAvailableDispatchers(authTokenRefresh, resolve);
  });
}

// ── User Tracking & Status ────────────────────────────────────────────

export function trackUser(latitude: string, longitude: string): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().trackUser(latitude, longitude, resolve, reject);
  });
}

export function securityCheck(id: string): Promise<any> {
  return new Promise((resolve) => {
    getApi().securityCheck(id, resolve);
  });
}

export function statusUpdate(data: {
  statusComment: string;
  latitude: number;
  longitude: number;
}): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().statusUpdate(data, resolve, reject);
  });
}

// ── Attachments ───────────────────────────────────────────────────────

export function saveAttachment(attachments: any[]): Promise<any> {
  return new Promise((resolve) => {
    getApi().saveAttachment(attachments, resolve);
  });
}

export function updateAttachment(newTitle: any, id: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().updateAttachment(newTitle, id, resolve);
  });
}

// ── User Management ───────────────────────────────────────────────────

export function getUserManagementList(
  start: number,
  perPage: string,
  data: string
): Promise<any> {
  return new Promise((resolve) => {
    getApi().getUserManagementList(start, perPage, data, resolve);
  });
}

export function updateUser(userId: any, accountEnabledState: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().updateUser(userId, accountEnabledState, resolve);
  });
}

export function saveUserManagement(body: any): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().saveUserManagement(body, resolve, reject);
  });
}

export function resendRegistration(userId: any): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().resendRegistration(userId, resolve, reject);
  });
}

export function getUserManagementAuditLogEntries(data: any): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().getUserManagementAuditLogEntries(data, resolve, reject);
  });
}

export function getUserManagementDownloadUserProfilesCsv(includeDisabledUsers: boolean): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().getUserManagementDownloadUserProfilesCsv(includeDisabledUsers, resolve, reject);
  });
}

// ── User Profile ──────────────────────────────────────────────────────

export function getUserName(): Promise<any> {
  return new Promise((resolve) => {
    getApi().getUserName(resolve);
  });
}

export function getUserProfile(userId: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().getUserProfile(userId, resolve);
  });
}

export function updateUserProfile(userId: any, userProfile: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().updateUserProfile(userId, userProfile, resolve);
  });
}

export function listUserProfileNoteAttachments(personId: number): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().listUserProfileNoteAttachments(personId, resolve, reject);
  });
}

export function userProfilePdf(userId: number): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().userProfilePdf(userId, resolve, reject);
  });
}

export function updateOfficerModuleEntry(data: {
  callSign: string | null;
  companyVehicleId: number | null;
}): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().updateOfficerModuleEntry(data, resolve, reject);
  });
}

// ── Registration & Password Reset ─────────────────────────────────────

export function validateUserName(data: any): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().validateUserName(data, resolve, reject);
  });
}

export function validateRegistrationCode(code: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().validateRegistrationCode(code, resolve);
  });
}

export function validatePasswordResetCode(code: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().validatePasswordResetCode(code, resolve);
  });
}

export function registerUser(data: string): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().registerUser(data, resolve, reject);
  });
}

export function resetPassword(data: string): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().resetPassword(data, resolve, reject);
  });
}

// ── Client Groups ─────────────────────────────────────────────────────

export function modifyClientGroup(id: any, isEnabled: boolean): Promise<any> {
  return new Promise((resolve) => {
    getApi().modifyClientGroup(id, isEnabled, resolve);
  });
}

export function getCGMng(start: number, perPage: string, data: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().getCGMng(start, perPage, data, resolve);
  });
}

export function listClientGroup(noLoadScreen: boolean = true): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().listClientGroup(resolve, reject, noLoadScreen);
  });
}

export function listClientGroupSite(id: any): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().listClientGroupSite(id, resolve, reject);
  });
}

export function listAllClientGroupSite(): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().listAllClientGroupSite(resolve, reject);
  });
}

export function updateCGMng(CGID: any, isEnabled: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().updateCGMng(CGID, isEnabled, resolve);
  });
}

export function getClientNotificationSummary(clientId: number): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().getClientNotificationSummary(clientId, resolve, reject);
  });
}

// ── Company Profile ───────────────────────────────────────────────────

export function getCompanyProfile(): Promise<any> {
  return new Promise((resolve) => {
    getApi().getCompanyProfile(resolve);
  });
}

export function updateCompanyProfile(company: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().updateCompanyProfile(company, resolve);
  });
}

// ── Messaging ─────────────────────────────────────────────────────────

export function getMessagingUserInfoList(): Promise<any> {
  return new Promise((resolve) => {
    getApi().getMessagingUserInfoList(resolve);
  });
}

export function submitCommand(command: string): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().submitCommand(command, resolve, reject);
  });
}

// ── Tours ─────────────────────────────────────────────────────────────

export function getTour(id: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().getTour(id, resolve);
  });
}

export function addTour(tour: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().addTour(tour, resolve);
  });
}

export function updateTour(tour: any, id: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().updateTour(tour, id, resolve);
  });
}

export function deleteTour(id: number): Promise<any> {
  return new Promise((resolve) => {
    getApi().deleteTour(id, resolve);
  });
}

export function enableTour(id: any, isEnabled: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().enableTour(id, isEnabled, resolve);
  });
}

export function getTours(start: number, perPage: string, data: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().getTours(start, perPage, data, resolve);
  });
}

export function getTourStopsForTour(tourId: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().getTourStopsForTour(tourId, resolve);
  });
}

export function reorderTourStopsForTour(tourId: number, tourStopIds: number[]): Promise<any> {
  return new Promise((resolve) => {
    getApi().reorderTourStopsForTour(tourId, tourStopIds, resolve);
  });
}

export function addTourStopToTour(tourId: any, tourStopId: any): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().addTourStopToTour(tourId, tourStopId, resolve, reject);
  });
}

// ── Tour Stops ────────────────────────────────────────────────────────

export function getTourStop(id: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().getTourStop(id, resolve);
  });
}

export function addTourStop(tourStop: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().addTourStop(tourStop, resolve);
  });
}

export function updateTourStop(tourStop: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().updateTourStop(tourStop, resolve);
  });
}

export function removeTourStop(tourId: any, tourStopId: any): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().removeTourStop(tourId, tourStopId, resolve, reject);
  });
}

export function getTourStops(start: number, perPage: string, data: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().getTourStops(start, perPage, data, resolve);
  });
}

export function deleteTourStop(id: any): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().deleteTourStop(id, resolve, reject);
  });
}

export function enableTourStop(id: any, isEnabled: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().enableTourStop(id, isEnabled, resolve);
  });
}

// ── Tasks (Guard Tour) ───────────────────────────────────────────────

export function getTourTasks(start: number, perPage: string, data: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().getTourTasks(start, perPage, data, resolve);
  });
}

export function addTask(task: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().addTask(task, resolve);
  });
}

export function updateTask(task: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().updateTask(task, resolve);
  });
}

export function deleteTask(id: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().deleteTask(id, resolve);
  });
}

export function completeTask(data: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().completeTask(data, resolve);
  });
}

export function updateCompleteGuardStop(completeId: any, officerComments: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().updateCompleteGuardStop(completeId, officerComments, resolve);
  });
}

// ── Tour Exemptions ───────────────────────────────────────────────────

export function getTourExemptions(data: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().getTourExemptions(data, resolve);
  });
}

export function saveTourExemption(tourExemption: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().saveTourExemption(tourExemption, resolve);
  });
}

export function updateTourExemption(tourExemption: any, revisiongroup: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().updateTourExemption(tourExemption, revisiongroup, resolve);
  });
}

export function deleteTourExemption(revisiongroup: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().deleteTourExemption(revisiongroup, resolve);
  });
}

export function enableTourExemption(revisiongroup: any, isEnabled: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().EnableTourExemption(revisiongroup, isEnabled, resolve);
  });
}

// ── Guard Tours ───────────────────────────────────────────────────────

export function getOpenGuardTours(start: any, perPage: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().getOpenGuardTours(start, perPage, resolve);
  });
}

export function getPendingGuardTours(start: any, perPage: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().getPendingGuardTours(start, perPage, resolve);
  });
}

export function getActiveGuardTours(start: any, perPage: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().getActiveGuardTours(start, perPage, resolve);
  });
}

export function getClosedGuardTours(start: any, perPage: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().getClosedGuardTours(start, perPage, resolve);
  });
}

export function claimGuardTour(id: number, isClaimed: boolean): Promise<any> {
  return new Promise((resolve) => {
    getApi().claimGuardTour(id, isClaimed, resolve);
  });
}

export function getGuardTourStopsForGuardTour(guardTourId: number): Promise<any> {
  return new Promise((resolve) => {
    getApi().getGuardTourStopsForGuardTour(guardTourId, resolve);
  });
}

export function getGuardTourStop(guardTourStopId: number): Promise<any> {
  return new Promise((resolve) => {
    getApi().getGuardTourStop(guardTourStopId, resolve);
  });
}

export function completeGuardTourStop(guardTourStopId: number, data: any): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().completeGuardTourStop(guardTourStopId, data, resolve, reject);
  });
}

export function getTourStopHistory(guardTourStopId: number, data: any): Promise<any> {
  return new Promise((resolve) => {
    getApi().getTourStopHistory(guardTourStopId, data, resolve);
  });
}

export function searchTourStopCompletions(data: string): Promise<any> {
  return new Promise((resolve) => {
    getApi().searchTourStopCompletions(data, resolve);
  });
}

export function getTourStopCompletionDetail(completionId: number): Promise<any> {
  return new Promise((resolve) => {
    getApi().getTourStopCompletionDetail(completionId, resolve);
  });
}

export function getTourStopCompletionOfficers(): Promise<any> {
  return new Promise((resolve) => {
    getApi().getTourStopCompletionOfficers(resolve);
  });
}

export function getTourStopCompletionTours(): Promise<any> {
  return new Promise((resolve) => {
    getApi().getTourStopCompletionTours(resolve);
  });
}

export function getTourStopCompletionPdf(completionId: number, timeZoneId: string): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().getTourStopCompletionPdf(completionId, timeZoneId, resolve, reject);
  });
}

export function getTourLogEntries(data: string, authTokenRefresh: boolean): Promise<any> {
  return new Promise((resolve) => {
    getApi().getTourLogEntries(data, authTokenRefresh, resolve);
  });
}

export function getGuardTourLogEntries(data: string, authTokenRefresh: boolean): Promise<any> {
  return new Promise((resolve) => {
    getApi().getGuardTourLogEntries(data, authTokenRefresh, resolve);
  });
}

// ── Map ───────────────────────────────────────────────────────────────

export function getMapLists(
  authTokenRefresh: boolean,
  noLoadScreen: boolean
): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().getMapLists(authTokenRefresh, noLoadScreen, resolve, reject);
  });
}

export function getMapOverlay(): Promise<MapOverlayGetResult> {
  return new Promise((resolve, reject) => {
    getApi().getMapOverlay(resolve, reject);
  });
}

export function updateMapOverlay(data: any): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().updateMapOverlay(data, resolve, reject);
  });
}

// ── Settings ──────────────────────────────────────────────────────────

export function getActivityReportSettings(): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().getActivityReportSettings(resolve, reject);
  });
}

export function updateActivityReportSettings(data: any): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().updateActivityReportSettings(data, resolve, reject);
  });
}

export function getCaseReportSettings(): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().getCaseReportSettings(resolve, reject);
  });
}

export function updateCaseReportSettings(data: any): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().updateCaseReportSettings(data, resolve, reject);
  });
}

export function getPersonSettings(): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().getPersonSettings(resolve, reject);
  });
}

export function updatePersonSettings(data: any): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().updatePersonSettings(data, resolve, reject);
  });
}

export function updateOfficerFilterPreference(isOfficerFilterOnlineOnly: boolean): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().updateOfficerFilterPreference(isOfficerFilterOnlineOnly, resolve, reject);
  });
}

export function getCallCardSettings(): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().getCallCardSettings(resolve, reject);
  });
}

export function updateCallCardSettings(data: any): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().updateCallCardSettings(data, resolve, reject);
  });
}

export function getCompanySettings(): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().getCompanySettings(resolve, reject);
  });
}

export function updateCompanySettings(data: any): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().updateCompanySettings(data, resolve, reject);
  });
}

// ── Call Dispositions ─────────────────────────────────────────────────

export function listGlobalCallDispositions(): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().listGlobalCallDispositions(resolve, reject);
  });
}

export function listCallDispositions(): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().listCallDispositions(resolve, reject);
  });
}

export function listCompanyCallDispositions(): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().listCompanyCallDispositions(resolve, reject);
  });
}

export function updateCompanyCallDispositionList(obj: object): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().updateCompanyCallDispositionList(obj, resolve, reject);
  });
}

export function addCustomCallDisposition(obj: object): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().addCustomCallDisposition(obj, resolve, reject);
  });
}

export function getDisabledDispositionCodesList(): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().getDisabledDispositionCodesList(resolve, reject);
  });
}

export function setDispositionCodeEnabled(data: object): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().setDispositionCodeEnabled(data, resolve, reject);
  });
}

// ── NOC List Management ───────────────────────────────────────────────

export function getDisabledNocList(): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().getDisabledNocList(resolve, reject);
  });
}

export function getSelfInitiateNocList(): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().getSelfInitiateNocList(resolve, reject);
  });
}

export function addSelfInitiateNoc(
  natureOfCall: number,
  isShownOnMobile: boolean,
  officerStatusLevel: string = 'ON_SCENE',
  secondaryNoc: number | null = null
): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().addSelfInitiateNoc(natureOfCall, isShownOnMobile, officerStatusLevel, secondaryNoc, resolve, reject);
  });
}

export function deleteSelfInitiateNoc(natureOfCall: number): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().deleteSelfInitiateNoc(natureOfCall, resolve, reject);
  });
}

export function updateSelfInitiateNoc(
  natureOfCall: number,
  isShownOnMobile: boolean,
  officerStatusLevel: string,
  secondaryNoc: number | null
): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().updateSelfInitiateNoc(natureOfCall, isShownOnMobile, officerStatusLevel, secondaryNoc, resolve, reject);
  });
}

export function setEnabledNocList(data: object): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().setEnabledNocList(data, resolve, reject);
  });
}

// ── Client Portal ─────────────────────────────────────────────────────

export function resendClientPortalRegistration(userId: any): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().resendClientPortalRegistration(userId, resolve, reject);
  });
}

export function getClientPortalUserList(): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().getClientPortalUserList(resolve, reject);
  });
}

export function addClientPortalUser(data: any): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().addClientPortalUser(data, resolve, reject);
  });
}

export function editClientPortalUser(data: any): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().editClientPortalUser(data, resolve, reject);
  });
}

export function deleteClientPortalUser(id: number): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().deleteClientPortalUser(id, resolve, reject);
  });
}

export function getClientPortalSettings(): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().getClientPortalSettings(resolve, reject);
  });
}

export function updateClientPortalSettings(data: any): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().updateClientPortalSettings(data, resolve, reject);
  });
}

export function getClientPortalLogEntries(data: string, authTokenRefresh: boolean): Promise<any> {
  return new Promise((resolve) => {
    getApi().getClientPortalLogEntries(data, authTokenRefresh, resolve);
  });
}

// ── Payments ──────────────────────────────────────────────────────────

export function getPaymentManagementInfo(): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().getPaymentManagementInfo(resolve, reject);
  });
}

export function renewPaymentSubscription(): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().renewPaymentSubscription(resolve, reject);
  });
}

export function cancelPaymentSubscription(data: any): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().cancelPaymentSubscription(data, resolve, reject);
  });
}

export function getSetupIntentClientSecret(attachToCurrentCompany: boolean): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().getSetupIntentClientSecret(attachToCurrentCompany, resolve, reject);
  });
}

export function getPricePlans(): Promise<any> {
  return new Promise((resolve) => {
    getApi().getPricePlans(resolve);
  });
}

export function updateDefaultCardPaymentMethod(data: any): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().updateDefaultCardPaymentMethod(data, resolve, reject);
  });
}

// ── Attachments & Documents ───────────────────────────────────────────

export function getOrnaSupportAttachmentsList(): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().getOrnaSupportAttachmentsList(resolve, reject);
  });
}

export function getCompanyDocumentsList(): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().getCompanyDocumentsList(resolve, reject);
  });
}

export function addCompanyDocument(data: any): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().addCompanyDocument(data, resolve, reject);
  });
}

export function removeCompanyDocument(attachmentId: number): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().removeCompanyDocument(attachmentId, resolve, reject);
  });
}

// ── Map Overlay KML ───────────────────────────────────────────────────

export function getMapOverlayKmlList(): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().getMapOverlayKmlList(resolve, reject);
  });
}

export function addMapOverlayKml(data: any): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().addMapOverlayKml(data, resolve, reject);
  });
}

export function removeMapOverlayKml(attachmentId: number): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().removeMapOverlayKml(attachmentId, resolve, reject);
  });
}

// ── Directory & Company Link ──────────────────────────────────────────

export function getOrnaDirectoryList(data: string): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().getOrnaDirectoryList(data, resolve, reject);
  });
}

export function getCompanyLinkList(authTokenRefresh: boolean): Promise<any> {
  return new Promise((resolve) => {
    getApi().getCompanyLinkList(authTokenRefresh, resolve);
  });
}

export function addCompanyLink(
  usernameAtOfficerCompany: string,
  authTokenRefresh: boolean
): Promise<any> {
  return new Promise((resolve) => {
    getApi().addCompanyLink(usernameAtOfficerCompany, authTokenRefresh, resolve);
  });
}

export function removeCompanyLink(
  otherCompanyId: number,
  otherCompanyType: string,
  authTokenRefresh: boolean
): Promise<any> {
  return new Promise((resolve) => {
    getApi().removeCompanyLink(otherCompanyId, otherCompanyType, authTokenRefresh, resolve);
  });
}

export function acceptCompanyLink(
  otherCompanyId: number,
  otherCompanyType: string,
  authTokenRefresh: boolean
): Promise<any> {
  return new Promise((resolve) => {
    getApi().acceptCompanyLink(otherCompanyId, otherCompanyType, authTokenRefresh, resolve);
  });
}

// ── Analytics ─────────────────────────────────────────────────────────

export function analyticsReportClientStartDates(): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().analyticsReportdClientStartDates(resolve, reject);
  });
}

export function analyticsReportPdf(data: any): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().analyticsReportdPdf(data, resolve, reject);
  });
}

// ── Error Logging ─────────────────────────────────────────────────────

export function insertNewJSError(
  fullError: string,
  url: string,
  errorMessage: string,
  type: string
): Promise<any> {
  return new Promise((resolve) => {
    getApi().insertNewJSError(fullError, url, errorMessage, type, resolve);
  });
}

// ── Version Checks ────────────────────────────────────────────────────

export function frontendVersionCheck(): Promise<string> {
  return new Promise((resolve) => {
    getApi().frontendVersionCheck(resolve);
  });
}

export function backendVersionCheck(): Promise<string> {
  return new Promise((resolve) => {
    getApi().backendVersionCheck(resolve);
  });
}

// ── Citations ─────────────────────────────────────────────────────────

export function searchCitations(
  filters: any,
  start: number,
  perPage: number
): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().searchCitations(filters, start, perPage, resolve, reject);
  });
}

export function getCitationById(id: number): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().getCitationById(id, resolve, reject);
  });
}

export function getCitationsForPerson(
  personId: number,
  filters: any,
  start: number,
  perPage: number
): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().getCitationsForPerson(personId, filters, start, perPage, resolve, reject);
  });
}

export function exportCitationsForPerson(personId: number, filters: any): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().exportCitationsForPerson(personId, filters, resolve, reject);
  });
}

export function getCitationsForCallCard(
  callCardId: number,
  filters: any,
  start: number,
  perPage: number
): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().getCitationsForCallCard(callCardId, filters, start, perPage, resolve, reject);
  });
}

export function saveCitation(citation: any): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().saveCitation(citation, resolve, reject);
  });
}

export function updateCitation(
  citation: any,
  lastKnownModifiedDate?: Date
): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().updateCitation(citation, lastKnownModifiedDate, resolve, reject);
  });
}

export function deleteCitation(id: number): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().deleteCitation(id, resolve, reject);
  });
}

export function issueCitation(id: number, lastKnownModifiedDate?: Date): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().issueCitation(id, lastKnownModifiedDate, resolve, reject);
  });
}

export function voidCitation(
  id: number,
  reason: string,
  lastKnownModifiedDate?: Date
): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().voidCitation(id, reason, lastKnownModifiedDate, resolve, reject);
  });
}

export function getCitationPdf(
  citationId: number,
  timeZoneId: string,
  version: 'public' | 'internal'
): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().getCitationPdf(citationId, timeZoneId, version, resolve, reject);
  });
}

export function exportCitations(filters: any): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().exportCitations(filters, resolve, reject);
  });
}

export function getCitationAuditLog(
  citationId: number,
  data: any,
  includeViews: boolean
): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().getCitationAuditLog(citationId, data, includeViews, resolve, reject);
  });
}

export function getCitationSettings(): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().getCitationSettings(resolve, reject);
  });
}

export function updateCitationSettings(settings: any): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().updateCitationSettings(settings, resolve, reject);
  });
}

// ── Violation Types ───────────────────────────────────────────────────

export function getViolationTypes(): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().getViolationTypes(resolve, reject);
  });
}

export function getCompanyViolationTypes(): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().getCompanyViolationTypes(resolve, reject);
  });
}

export function getGlobalViolationTypes(): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().getGlobalViolationTypes(resolve, reject);
  });
}

export function addCompanyViolationType(value: string): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().addCompanyViolationType(value, resolve, reject);
  });
}

export function updateViolationTypeList(
  violationTypeList: Array<{ code: number; listIdx: number }>
): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().updateViolationTypeList(violationTypeList, resolve, reject);
  });
}

// ── SOP (Standard Operating Procedures) ──────────────────────────────

export function getSOPList(obj: object): Promise<any> {
  return getStandardOperatingProcedures(obj);
}

// ── Feature Intros ────────────────────────────────────────────────────

/** Enable Advisory Board (grants advisory perms + marks the intro handled). */
export function enableAdvisory(): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().enableAdvisory(resolve, reject);
  });
}

/** Dismiss a feature intro by key (generic; marks the intro handled). */
export function dismissFeatureIntro(featureKey: string): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().dismissFeatureIntro(featureKey, resolve, reject);
  });
}

// ── Setup Wizard ──────────────────────────────────────────────────────

export interface SetupWizardChecklist {
  teamAdded: boolean;
  officersAdded: number;
  callCardCreated: boolean;
  tourCreated: boolean;
  reportSubmitted: boolean;
}

export interface SetupWizardStatus {
  eligible: boolean;
  completed: boolean;
  pending: boolean;
  vertical: string | null;
  fieldStaffBand: string | null;
  checklist: SetupWizardChecklist;
}

/** Wizard + getting-started checklist state (admins only; 403 for others). */
export function getSetupWizardStatus(): Promise<SetupWizardStatus> {
  return new Promise((resolve, reject) => {
    getApi().getSetupWizardStatus(resolve, reject);
  });
}

/** Apply the setup wizard's answers and mark it complete. */
export function completeSetupWizard(body: {
  vertical: string;
  fieldStaffBand: string;
  useTours: boolean;
  useCitations: boolean;
  useClientPortal: boolean;
  useAdvisories: boolean;
}): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().completeSetupWizard(body, resolve, reject);
  });
}

/**
 * Autosave the wizard's in-progress profile answers (vertical / team size).
 * Non-destructive: does not apply terminology, revoke modules, or complete the wizard,
 * so it's safe to fire on every pick. See
 * docs/decisions/2026-07-07-setup-wizard-draft-autosave.md.
 */
export function saveSetupWizardDraft(body: {
  vertical?: string;
  fieldStaffBand?: string;
}): Promise<any> {
  return new Promise((resolve, reject) => {
    getApi().saveSetupWizardDraft(body, resolve, reject);
  });
}
